import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Spinner } from '@heroui/react';

import { useApi } from '../lib/api-context';
import type { MacDevice } from '../lib/api-client';
import { gatewayBaseUrl } from '../lib/env';

const POLL_INTERVAL_MS = 5_000;

interface AddMacModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * "Add a Mac" modal — single-page pairing flow per DD-MAC-WEB-01.
 * Three sections:
 *   1. Install command (curl|bash) — one click to copy.
 *   2. Pairing code — Generate button mints a fresh 8-char code via
 *      `POST /api/mac/issue-code`; UI shows the code + a 5-min
 *      countdown.
 *   3. Paired devices — polled every 5 s while the modal is open
 *      (DD-MAC-WEB-03). Each row has Revoke (DELETE
 *      /api/mac/devices/:id).
 *
 * Tailwind-styled, matching the ConfirmDeleteDialog idiom in
 * LeftSidebar.tsx (HeroUI v3's Modal is awkward to drive
 * imperatively from a parent's `isOpen` prop).
 */
export function AddMacModal({ isOpen, onClose }: AddMacModalProps) {
  const api = useApi();
  const [code, setCode] = useState<{ code: string; expiresAt: number } | null>(
    null,
  );
  const [issuing, setIssuing] = useState(false);
  const [issueError, setIssueError] = useState<string | null>(null);
  const [devices, setDevices] = useState<MacDevice[] | null>(null);
  const [devicesError, setDevicesError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const installCommand = useMemo(() => {
    const base = gatewayBaseUrl().replace(/\/$/, '');
    return `/bin/bash -c "$(curl -fsSL ${base}/api/mac/install.sh)"`;
  }, []);

  // Esc closes.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Poll device list every 5s while the modal is open. Skip the
  // initial fetch's flash by setting `loading` only when we don't
  // have any data yet.
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const fetchDevices = async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const list = await api.listMacDevices({ signal: controller.signal });
        if (!cancelled) {
          setDevices(list);
          setDevicesError(null);
        }
      } catch (e) {
        if (cancelled) return;
        if ((e as Error).name === 'AbortError') return;
        setDevicesError(e instanceof Error ? e.message : String(e));
      }
    };
    void fetchDevices();
    const id = setInterval(fetchDevices, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
      abortRef.current?.abort();
    };
  }, [api, isOpen]);

  const handleGenerateCode = useCallback(async () => {
    setIssuing(true);
    setIssueError(null);
    try {
      const result = await api.issueMacPairingCode();
      setCode(result);
    } catch (e) {
      setIssueError(e instanceof Error ? e.message : String(e));
    } finally {
      setIssuing(false);
    }
  }, [api]);

  const handleRevoke = useCallback(
    async (deviceId: string) => {
      setRevokingId(deviceId);
      try {
        await api.revokeMacDevice(deviceId);
        setDevices((cur) =>
          cur ? cur.filter((d) => d.deviceId !== deviceId) : cur,
        );
      } catch (e) {
        setDevicesError(e instanceof Error ? e.message : String(e));
      } finally {
        setRevokingId(null);
      }
    },
    [api],
  );

  const handleCopyInstall = useCallback(() => {
    void navigator.clipboard.writeText(installCommand);
  }, [installCommand]);

  const handleCopyCode = useCallback(() => {
    if (code) void navigator.clipboard.writeText(code.code);
  }, [code]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add a Mac"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-xl bg-zinc-900 ring-1 ring-white/10 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div className="text-sm font-semibold">Add a Mac</div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-0.5 text-xs text-zinc-300 hover:bg-white/5"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Step 1 — Install */}
        <Section
          title="1. Install the Mac client"
          subtitle="Run on the Mac you want to expose. No sudo required."
        >
          <CommandBlock
            command={installCommand}
            onCopy={handleCopyInstall}
          />
          <p className="mt-2 text-xs text-zinc-400">
            Installs <code>leibniz-mac</code> at <code>~/.local/bin</code>,
            CuaDriver.app at <code>/Applications</code>, plus a LaunchAgent
            so the daemon starts on login. Skip this step if the Mac is
            already installed.
          </p>
        </Section>

        {/* Step 2 — Code */}
        <Section
          title="2. Generate a pairing code"
          subtitle="Single-use, expires in 5 minutes. Run leibniz-mac pair <CODE> on the Mac."
        >
          {!code && (
            <Button
              variant="primary"
              onPress={handleGenerateCode}
              isDisabled={issuing}
            >
              {issuing ? 'Generating…' : 'Generate code'}
            </Button>
          )}
          {code && (
            <CodeDisplay
              code={code.code}
              expiresAt={code.expiresAt}
              onCopy={handleCopyCode}
              onRegenerate={handleGenerateCode}
              regenerating={issuing}
            />
          )}
          {issueError && (
            <p className="mt-2 text-xs text-rose-300">{issueError}</p>
          )}
        </Section>

        {/* Step 3 — Devices */}
        <Section
          title="3. Paired devices"
          subtitle="Refreshes every 5 seconds. New pairings appear here automatically."
        >
          <DevicesList
            devices={devices}
            error={devicesError}
            revokingId={revokingId}
            onRevoke={handleRevoke}
          />
        </Section>

        <div className="flex items-center justify-end gap-2 border-t border-white/10 px-4 py-3">
          <Button variant="ghost" onPress={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-white/5 px-4 py-4">
      <div className="text-sm font-medium">{title}</div>
      <div className="text-xs text-zinc-400">{subtitle}</div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function CommandBlock({
  command,
  onCopy,
}: {
  command: string;
  onCopy: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 truncate rounded-md bg-black/40 px-3 py-2 text-xs font-mono text-zinc-200 ring-1 ring-white/5">
        {command}
      </code>
      <Button size="sm" variant="ghost" onPress={onCopy}>
        Copy
      </Button>
    </div>
  );
}

function CodeDisplay({
  code,
  expiresAt,
  onCopy,
  onRegenerate,
  regenerating,
}: {
  code: string;
  expiresAt: number;
  onCopy: () => void;
  onRegenerate: () => void;
  regenerating: boolean;
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, []);
  const remainingMs = Math.max(0, expiresAt - now);
  const minutes = Math.floor(remainingMs / 60_000);
  const seconds = Math.floor((remainingMs % 60_000) / 1_000)
    .toString()
    .padStart(2, '0');
  const expired = remainingMs <= 0;
  return (
    <div>
      <div className="flex items-center gap-3">
        <code
          className="rounded-md bg-black/40 px-4 py-3 font-mono text-2xl tracking-[0.2em] text-zinc-100 ring-1 ring-white/5"
          aria-label="pairing code"
        >
          {code}
        </code>
        <Button size="sm" variant="ghost" onPress={onCopy}>
          Copy
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onPress={onRegenerate}
          isDisabled={regenerating}
        >
          {regenerating ? 'Regenerating…' : 'Regenerate'}
        </Button>
      </div>
      <p className="mt-2 text-xs text-zinc-400">
        {expired ? (
          <span className="text-rose-300">Expired — regenerate to continue.</span>
        ) : (
          <>
            Expires in{' '}
            <span className="font-mono text-zinc-200">
              {minutes}:{seconds}
            </span>
            . On the Mac, run{' '}
            <code className="font-mono text-zinc-200">
              leibniz-mac pair {code}
            </code>
            .
          </>
        )}
      </p>
    </div>
  );
}

function DevicesList({
  devices,
  error,
  revokingId,
  onRevoke,
}: {
  devices: MacDevice[] | null;
  error: string | null;
  revokingId: string | null;
  onRevoke: (deviceId: string) => void;
}) {
  if (devices === null && error === null) {
    return (
      <div className="flex items-center gap-2 text-xs text-zinc-400">
        <Spinner size="sm" /> Loading paired devices…
      </div>
    );
  }
  if (error) {
    return <p className="text-xs text-rose-300">{error}</p>;
  }
  if (!devices || devices.length === 0) {
    return (
      <p className="text-xs text-zinc-400">
        No Macs paired yet. Generate a code above and run{' '}
        <code className="font-mono text-zinc-200">leibniz-mac pair &lt;CODE&gt;</code>{' '}
        on the Mac to pair.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-white/5 rounded-md ring-1 ring-white/5">
      {devices.map((d) => (
        <li
          key={d.deviceId}
          className="flex items-center gap-3 px-3 py-2 text-sm"
        >
          <span
            className={
              d.online
                ? 'h-2 w-2 rounded-full bg-emerald-400'
                : 'h-2 w-2 rounded-full bg-zinc-600'
            }
            aria-label={d.online ? 'online' : 'offline'}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="truncate font-medium">{d.displayName}</span>
              {d.label && (
                <span
                  className="truncate font-mono text-[11px] text-zinc-500"
                  title="Device label used for skill namespacing (DD-MAC-36)"
                >
                  {d.label}
                </span>
              )}
            </div>
            <div className="truncate text-xs text-zinc-400">
              {d.os} {d.osVersion} · paired{' '}
              {new Date(d.pairedAt).toLocaleDateString()}
              {d.lastSeenAt &&
                ` · last seen ${new Date(d.lastSeenAt).toLocaleString()}`}
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onPress={() => onRevoke(d.deviceId)}
            isDisabled={revokingId === d.deviceId}
          >
            {revokingId === d.deviceId ? 'Revoking…' : 'Revoke'}
          </Button>
        </li>
      ))}
    </ul>
  );
}
